import { useEffect, useRef, useState } from "react";

import { AgentAvailableContactSlotsResponseAvailableContactSlots } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

interface UseGetAgentAvailableContactSlots {
  availableContactSlots: AgentAvailableContactSlotsResponseAvailableContactSlots[];
  loading: boolean;
}

// 認定プランナーの連絡対応可能枠を取得する。
// 希望日程の入力自体は任意でスキップ可能だが、取得に失敗して選択肢がゼロになると
// 入力しようとしたユーザーの体験として不成立になるため、「制限なし（空配列）」に倒す（fail-open）。
// 空配列はサーバ側でも「全枠対応可（未設定）」を意味するので、成功・失敗で扱いが変わらない
export const useGetAgentAvailableContactSlots = (agentId?: number): UseGetAgentAvailableContactSlots => {
  const [availableContactSlots, setAvailableContactSlots] = useState<
    AgentAvailableContactSlotsResponseAvailableContactSlots[]
  >([]);
  const [loading, setLoading] = useState(false);

  // モーダルは応答前に閉じられうる。React 17 ではアンマウント後の setState が console.error 警告になり、
  // src/index.tsx の CaptureConsole 経由で Sentry イベントになるためガードする
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!agentId) {
      setAvailableContactSlots([]);
      // agentId が「値→undefined」に変わった場合に loading=true のまま固まらないよう明示的に戻す
      setLoading(false);
      return;
    }

    let isCurrent = true;
    setLoading(true);

    const fetch = async () => {
      try {
        const fp = PropallyApiFactory();
        const res = await fp.v1AgentsAgentIdAvailableContactSlotsGet(agentId, { withCredentials: true });
        const slots = res.data?.available_contact_slots;
        if (isCurrent && mountedRef.current) setAvailableContactSlots(Array.isArray(slots) ? slots : []);
      } catch {
        // Sentry へは CustomApiFactory の interceptor が既に送っている。
        // ここでは全枠選択可へ倒すだけで、ユーザーには制限なしとして見せる
        if (isCurrent && mountedRef.current) setAvailableContactSlots([]);
      } finally {
        if (isCurrent && mountedRef.current) setLoading(false);
      }
    };

    void fetch();

    return () => {
      isCurrent = false;
    };
  }, [agentId]);

  return { availableContactSlots, loading };
};
